import { ApiResponse } from 'common/domain/entities/api-response';
import React, {
	createContext, FC, useContext, useMemo, useState,
} from 'react';
import { AddCataglogModel, CatelogBackofficeModel } from '../../data/dto/catelog-backoffice-model';
import { FilterParams } from '../../data/remote/catelog-api';
import { CatelogApiImpl } from '../../data/remote/catelog-api-impl';

interface FetchState {
	loading: boolean;
	error: string;
}

type TContextProps = {
	catelogs: CatelogBackofficeModel[]
	catelogFetchState: FetchState;
	getCatelogs: (params: FilterParams) => Promise<boolean>
	setCatelogs: React.Dispatch<React.SetStateAction<CatelogBackofficeModel[]>>;
	changeCatelogStatus: (id: number, active: boolean) => Promise<{
		success: boolean;
		message: string;
	}>
	updatingStatus: boolean;

	catalog: AddCataglogModel;
	setCatalog: React.Dispatch<React.SetStateAction<AddCataglogModel>>;
	getCatalogById: (id: number) => Promise<boolean>;
	addEditCatalogState: FetchState;
	createCatalog: (catelog: AddCataglogModel) => Promise<boolean>;
	updateCatalog: (id: number, catelog: AddCataglogModel) => Promise<boolean>;
	catalogErrors: AddCataglogModel;
	setCatalogErrors: React.Dispatch<React.SetStateAction<AddCataglogModel>>;
}

const CatelogContext = createContext({} as TContextProps);

export const CatelogProvider: FC<React.PropsWithChildren> = ({ children }) => {
	const [catelogs, setCatelogs] = useState([] as CatelogBackofficeModel[]);
	const [catelogFetchState, setCatelogFetchState] = useState<FetchState>({
		loading: false,
		error: '',
	});
	const [updatingStatus, setUpdatingStatus] = useState(false);
	const [catalog, setCatalog] = useState<AddCataglogModel>({
		name: '',
		fileUrl: '',
		imageUrl: '',
	} as AddCataglogModel);
	const [catalogErrors, setCatalogErrors] = useState<AddCataglogModel>({
		name: '',
		fileUrl: '',
		imageUrl: '',
	} as AddCataglogModel);
	const [addEditCatalogState, setAddEditCatalogState] = useState<FetchState>({
		loading: false,
		error: '',
	});

	const catelogApi = new CatelogApiImpl();

	const getCatelogs = (params: FilterParams) => {
		setCatelogFetchState({
			...catelogFetchState,
			loading: true,
		});

		return catelogApi.getCatelogs(params).then((res) => {
			setCatelogs(res.data);
			return true;
		}).catch((err) => {
			const responseError = err as ApiResponse<void>;
			setCatelogFetchState((prev: FetchState) => ({ ...prev, error: responseError.message }));
			return false;
		}).finally(() => {
			setCatelogFetchState((prev: FetchState) => ({ ...prev, loading: false }));
		});
	};

	const changeCatelogStatus = (id: number, active: boolean) => {
		setUpdatingStatus(true);
		return catelogApi.changeCatelogStatus(id, active).then((res) => ({
			success: true, message: res.message,
		})).catch((err) => {
			const responseError = err as ApiResponse<void>;
			return {
				success: false, message: responseError.message,
			};
		}).finally(() => {
			setUpdatingStatus(false);
		});
	};

	const getCatalogById = (id: number) => {
		setAddEditCatalogState({
			loading: true,
			error: '',
		});
		return catelogApi.getCatelogById(id).then((res) => {
			setCatalog({
				name: res.data.name,
				fileUrl: res.data.fileUrl,
				imageUrl: res.data.imageUrl,
			});
			return true;
		}).catch((err) => {
			const responseError = err as ApiResponse<void>;
			setAddEditCatalogState((prev: FetchState) => ({ ...prev, error: responseError.message }));
			return false;
		}).finally(() => {
			setAddEditCatalogState({
				loading: false,
				error: '',
			});
		});
	};

	const createCatalog = (catelog: AddCataglogModel) => {
		setAddEditCatalogState({
			loading: true,
			error: '',
		});
		return catelogApi.addCatelog(catelog).then(() => true).catch((err) => {
			const responseError = err as ApiResponse<void>;
			setAddEditCatalogState((prev: FetchState) => ({ ...prev, error: responseError.message }));
			return false;
		}).finally(() => {
			setAddEditCatalogState({
				loading: false,
				error: '',
			});
		});
	};

	const updateCatalog = (id: number, catelog: AddCataglogModel) => {
		setAddEditCatalogState({
			loading: true,
			error: '',
		});
		return catelogApi.updateCatelog(id, catelog).then((res) => {
			setCatalog({
				name: res.data.name,
				fileUrl: res.data.fileUrl,
				imageUrl: res.data.imageUrl,
			});
			return true;
		}).catch((err) => {
			const responseError = err as ApiResponse<void>;
			setAddEditCatalogState((prev: FetchState) => ({ ...prev, error: responseError.message }));
			return false;
		}).finally(() => {
			setAddEditCatalogState({
				loading: false,
				error: '',
			});
		});
	};

	const value = useMemo(() => ({
		getCatelogs,
		setCatelogs,
		catelogs,
		catelogFetchState,
		changeCatelogStatus,
		updatingStatus,
		catalog,
		setCatalog,
		getCatalogById,
		addEditCatalogState,
		createCatalog,
		updateCatalog,
		catalogErrors,
		setCatalogErrors,
	}), [catelogs,
		catelogFetchState,
		addEditCatalogState,
		catalog,
		catalogErrors,
		updatingStatus]);
	return <CatelogContext.Provider value={value}>{children}</CatelogContext.Provider>;
};

export function useCatelog() {
	return useContext(CatelogContext);
}
